import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Pencil, Check, Ban, Star, Loader2, ChevronsUpDown } from "lucide-react";
import { Country, City } from "country-state-city";
import { ComboBox } from "../../components/ComboBox";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canManageMarketplace } from "../../lib/marketplaceAccess";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../../components/ui/command";
import ImageUpload from "../../components/ImageUpload";
import { toast } from "sonner";

// 20 most-common languages — toggled like categories on the supplier form.
const LANGUAGES = [
  "English", "Mandarin Chinese", "Hindi", "Spanish", "French", "Arabic",
  "Bengali", "Russian", "Portuguese", "Indonesian", "German", "Japanese",
  "Turkish", "Italian", "Korean", "Vietnamese", "Greek", "Dutch",
  "Polish", "Urdu",
];



const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  suspended: "bg-zinc-100 text-zinc-600",
  rejected: "bg-rose-50 text-rose-700",
};

const BLANK = {
  company_name: "", legal_name: "", trade_license: "", vat_number: "", tax_id: "", description: "",
  website: "", logo: "", cover_image: "", year_founded: "", employees: "",
  country: "", city: "", postcode: "", address: "", maps_url: "", latitude: null, longitude: null, place_id: "", languages: [],
  category_ids: [], specialty_ids: [],
  emergency_247: false, weekend_service: false, night_service: false,
  response_time: "", hourly_rate: "", services: [], contacts: [],
  bank_name: "", bank_account_holder: "", bank_account_number: "",
  bank_iban: "", bank_bic_swift: "", bank_details_visible: false,
};

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Bold green section headline with breathing room below.
function SectionTitle({ children }) {
  return <h3 className="text-sm font-bold text-emerald-600 mb-2">{children}</h3>;
}

export function SupplierFormBody({ editing, categories, specialties, onSaved, onClose, onCancel, selfServe = false, inline = false }) {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");

  const linkUser = async () => {
    try {
      const { data } = await api.post(`/marketplace/admin/suppliers/${editing.id}/link-user`, { email: loginEmail.trim() });
      toast.success(data.user_id ? `Linked to ${data.name || "user"}` : "Login unlinked");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (editing) {
      setF({
        ...BLANK, ...editing,
        languages: editing.languages || [],
        address: editing.address || editing.marina || "",
        year_founded: editing.year_founded ?? "",
        employees: editing.employees ?? "",
        hourly_rate: editing.hourly_rate ?? "",
        category_ids: editing.category_ids || [],
        specialty_ids: editing.specialty_ids || [],
        services: editing.services || [],
        contacts: editing.contacts || [],
      });
    } else {
      setF(BLANK);
    }
  }, [editing]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const toggleId = (key, id) => setF((p) => ({
    ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id],
  }));
  const toggleLang = (lang) => setF((p) => ({
    ...p, languages: p.languages.includes(lang) ? p.languages.filter((x) => x !== lang) : [...p.languages, lang],
  }));
  const addService = () => setF((p) => ({ ...p, services: [...p.services, { name: "", description: "", starting_price: null }] }));
  const setService = (i, k, v) => setF((p) => ({ ...p, services: p.services.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const rmService = (i) => setF((p) => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));
  const addContact = () => setF((p) => ({ ...p, contacts: [...(p.contacts || []), { name: "", position: "", email: "", phone: "", whatsapp: false }] }));
  const setContact = (i, k, v) => setF((p) => ({ ...p, contacts: p.contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmContact = (i) => setF((p) => ({ ...p, contacts: p.contacts.filter((_, idx) => idx !== i) }));

  // Country / city option lists (offline dataset). Picking a country narrows
  // the city list; anything missing can still be typed via the combobox.
  const countryNames = useMemo(() => Country.getAllCountries().map((c) => c.name), []);
  const nameToIso = useMemo(
    () => Object.fromEntries(Country.getAllCountries().map((c) => [c.name, c.isoCode])), [],
  );
  const cityNames = useMemo(() => {
    const iso = nameToIso[f.country];
    if (!iso) return [];
    return [...new Set((City.getCitiesOfCountry(iso) || []).map((c) => c.name))];
  }, [f.country, nameToIso]);

  const save = async () => {
    if (!f.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        languages: f.languages,
        year_founded: f.year_founded ? Number(f.year_founded) : null,
        employees: f.employees ? Number(f.employees) : null,
        hourly_rate: f.hourly_rate ? Number(f.hourly_rate) : null,
        services: f.services.filter((s) => (s.name || "").trim()).map((s) => ({
          ...s, starting_price: s.starting_price ? Number(s.starting_price) : null,
        })),
        contacts: (f.contacts || []).filter((c) => (c.name || c.email || c.phone || "").trim()),
      };
      if (selfServe) await api.patch("/marketplace/my-supplier", payload);
      else if (editing) await api.patch(`/marketplace/admin/suppliers/${editing.id}`, payload);
      else await api.post("/marketplace/admin/suppliers", payload);
      toast.success(selfServe ? "Profile updated" : (editing ? "Supplier updated" : "Supplier created (pending approval)"));
      onSaved();
      if (onClose) onClose();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company name *"><Input value={f.company_name} onChange={set("company_name")} data-testid="sf-company-name" /></Field>
          <Field label="Legal name"><Input value={f.legal_name} onChange={set("legal_name")} /></Field>
          <Field label="Trade license"><Input value={f.trade_license} onChange={set("trade_license")} /></Field>
          <Field label="VAT number"><Input value={f.vat_number} onChange={set("vat_number")} /></Field>
          <Field label="Tax ID number"><Input value={f.tax_id} onChange={set("tax_id")} data-testid="sf-tax-id" /></Field>
          <Field label="Country"><ComboBox value={f.country} onChange={(v) => setF((p) => ({ ...p, country: v, city: "" }))} options={countryNames} placeholder="Select country" testid="sf-country" /></Field>
          <Field label="City"><ComboBox value={f.city} onChange={(v) => setF((p) => ({ ...p, city: v }))} options={cityNames} placeholder={f.country ? "Select or add city" : "Type a city"} testid="sf-city" /></Field>
          <Field label="Postcode"><Input value={f.postcode} onChange={set("postcode")} data-testid="sf-postcode" /></Field>
          <Field label="Website"><Input value={f.website} onChange={set("website")} /></Field>
          <Field label="Year founded"><Input type="number" value={f.year_founded} onChange={set("year_founded")} /></Field>
          <Field label="Employees"><Input type="number" value={f.employees} onChange={set("employees")} /></Field>
          <Field label="Response time"><Input value={f.response_time} onChange={set("response_time")} placeholder="e.g. < 2h" /></Field>
          <Field label="Hourly rate"><Input type="number" value={f.hourly_rate} onChange={set("hourly_rate")} /></Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address"><Input value={f.address} onChange={set("address")} placeholder="Street / marina, building, unit" data-testid="sf-address" /></Field>
        </div>

        <Field label="Description"><Textarea rows={3} value={f.description} onChange={set("description")} /></Field>

        {editing && !selfServe && (
          <Field label="Supplier login — link an existing user account by email (they can then manage quotes in the Supplier hub)">
            {(editing.email || editing.contacts?.[0]?.email) && (
              <p className="text-xs text-zinc-500 mb-1.5" data-testid="sf-primary-email">
                Supplier's contact email: <b className="text-zinc-700">{editing.email || editing.contacts?.[0]?.email}</b>
                {" "}— shown under "Contact details" below. The field here is only for linking a Seavanto login.
              </p>
            )}
            <div className="flex gap-2">
              <Input value={loginEmail || ""} onChange={(e) => setLoginEmail(e.target.value)} placeholder="Login email of an existing user — leave blank to unlink" data-testid="sf-link-email" />
              <Button type="button" variant="outline" onClick={linkUser} data-testid="sf-link-btn">Link</Button>
            </div>
            {editing.user_id && <p className="text-xs text-emerald-600 mt-1">A login is currently linked.</p>}
          </Field>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Logo"><ImageUpload value={f.logo} onChange={(p) => setF((x) => ({ ...x, logo: p }))} folder="marketplace" testid="sf-logo" aspect="aspect-square" label="logo" /></Field>
          <Field label="Cover image"><ImageUpload value={f.cover_image} onChange={(p) => setF((x) => ({ ...x, cover_image: p }))} folder="marketplace" testid="sf-cover" label="cover" /></Field>
        </div>

        <div>
          <SectionTitle>Categories</SectionTitle>
          <div className="flex flex-wrap gap-1.5" data-testid="sf-categories">
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={() => toggleId("category_ids", c.id)}
                className={`text-xs px-2 py-1 rounded-full border ${f.category_ids.includes(c.id) ? "bg-ink text-white border-ink" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionTitle>Specialties / Brands</SectionTitle>
          <div className="flex flex-wrap gap-1.5" data-testid="sf-specialties">
            {specialties.map((s) => (
              <button key={s.id} type="button" onClick={() => toggleId("specialty_ids", s.id)}
                className={`text-xs px-2 py-1 rounded-full border ${f.specialty_ids.includes(s.id) ? "bg-sky-600 text-white border-sky-600" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
                {s.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionTitle>Languages</SectionTitle>
          <div className="flex flex-wrap gap-1.5" data-testid="sf-languages">
            {LANGUAGES.map((lang) => (
              <button key={lang} type="button" onClick={() => toggleLang(lang)}
                className={`text-xs px-2 py-1 rounded-full border ${f.languages.includes(lang) ? "bg-ink text-white border-ink" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`sf-lang-${lang}`}>
                {lang}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="sf-contacts">
          <div className="flex items-center justify-between">
            <SectionTitle>Contact details</SectionTitle>
            <Button type="button" size="sm" variant="outline" onClick={addContact} data-testid="sf-add-contact"><Plus className="h-3 w-3 mr-1" />Add contact</Button>
          </div>
          <div className="mt-2 space-y-3">
            {(f.contacts || []).map((c, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3" data-testid={`sf-contact-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input value={c.name || ""} onChange={(e) => setContact(i, "name", e.target.value)} placeholder="Name" disabled={!!c.account} data-testid={`sf-contact-name-${i}`} />
                  <Input value={c.position || ""} onChange={(e) => setContact(i, "position", e.target.value)} placeholder="Title / role" disabled={!!c.account} data-testid={`sf-contact-title-${i}`} />
                  <Input value={c.email || ""} onChange={(e) => setContact(i, "email", e.target.value)} placeholder="Email" disabled={!!c.account} data-testid={`sf-contact-email-${i}`} />
                  <Input value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} placeholder="Phone" disabled={!!c.account} data-testid={`sf-contact-phone-${i}`} />
                </div>
                {!!c.account && (
                  <p className="mt-1.5 text-[11px] text-zinc-400" data-testid={`sf-contact-synced-${i}`}>
                    Name, email and phone are taken from Account preferences; the title mirrors the Team-page role. They update automatically when those change.
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!c.whatsapp} onCheckedChange={(v) => setContact(i, "whatsapp", v)} data-testid={`sf-contact-whatsapp-${i}`} />
                    Phone is on WhatsApp
                  </label>
                  {!c.account && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => rmContact(i)} data-testid={`sf-contact-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  )}
                </div>
              </div>
            ))}
            {(f.contacts || []).length === 0 && <p className="text-xs text-zinc-400">No contacts yet — add one so owners know who to reach.</p>}
          </div>
        </div>

        <div data-testid="sf-bank">
          <SectionTitle>Bank details</SectionTitle>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Bank name"><Input value={f.bank_name} onChange={set("bank_name")} data-testid="sf-bank-name" /></Field>
            <Field label="Account holder"><Input value={f.bank_account_holder} onChange={set("bank_account_holder")} data-testid="sf-bank-holder" /></Field>
            <Field label="Account number"><Input value={f.bank_account_number} onChange={set("bank_account_number")} data-testid="sf-bank-account" /></Field>
            <Field label="IBAN"><Input value={f.bank_iban} onChange={set("bank_iban")} data-testid="sf-bank-iban" /></Field>
            <Field label="BIC / SWIFT"><Input value={f.bank_bic_swift} onChange={set("bank_bic_swift")} data-testid="sf-bank-bic" /></Field>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm" data-testid="sf-bank-visible-label">
            <Checkbox checked={f.bank_details_visible} onCheckedChange={(v) => setF((p) => ({ ...p, bank_details_visible: !!v }))} data-testid="sf-bank-visible" />
            Make bank details available to active clients (yachts with an accepted quote)
          </label>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.emergency_247} onCheckedChange={(v) => setF((p) => ({ ...p, emergency_247: v }))} data-testid="sf-emergency" /> 24/7 Emergency</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.weekend_service} onCheckedChange={(v) => setF((p) => ({ ...p, weekend_service: v }))} /> Weekend</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.night_service} onCheckedChange={(v) => setF((p) => ({ ...p, night_service: v }))} /> Night</label>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <SectionTitle>Services</SectionTitle>
            <Button type="button" size="sm" variant="outline" onClick={addService} data-testid="sf-add-service"><Plus className="h-3 w-3 mr-1" />Add</Button>
          </div>
          <div className="mt-2 space-y-2">
            {f.services.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_100px_32px] gap-2 items-center">
                <Input value={s.name} onChange={(e) => setService(i, "name", e.target.value)} placeholder="Service name" />
                <Input value={s.description || ""} onChange={(e) => setService(i, "description", e.target.value)} placeholder="Description" />
                <Input type="number" value={s.starting_price ?? ""} onChange={(e) => setService(i, "starting_price", e.target.value)} placeholder="Price" />
                <Button type="button" variant="ghost" size="icon" onClick={() => rmService(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
          </div>
        </div>

        {inline ? (
          <div className="mt-6 flex justify-end gap-2" data-testid="company-form-footer">
            <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="sf-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-ink hover:bg-black" data-testid="sf-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing || selfServe ? "Save changes" : "Create supplier")}
            </Button>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="sf-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-ink hover:bg-black" data-testid="sf-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing || selfServe ? "Save changes" : "Create supplier")}
            </Button>
          </DialogFooter>
        )}
    </div>
  );
}

export function SupplierForm({ open, onClose, editing, categories, specialties, onSaved, selfServe = false }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="supplier-form">
        <DialogHeader><DialogTitle>{selfServe ? "Edit company profile" : (editing ? "Edit supplier" : "New supplier")}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Marketplace supplier company profile form</DialogDescription>
        {open && (
          <SupplierFormBody
            editing={editing} categories={categories} specialties={specialties}
            onSaved={onSaved} onClose={onClose} selfServe={selfServe}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaxonomyManager({ kind }) {
  const isCat = kind === "categories";
  const base = isCat ? "/marketplace/admin/categories" : "/marketplace/admin/specialties";
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");

  const load = async () => { try { const { data } = await api.get(base); setRows(data); } catch (e) { toast.error(fmtErr(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(base, { name: name.trim(), active: true }); setName(""); load(); toast.success("Added"); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const toggleActive = async (r) => { try { await api.patch(`${base}/${r.id}`, { name: r.name, active: !r.active, sort_order: r.sort_order || 0 }); load(); } catch (e) { toast.error(fmtErr(e)); } };
  const del = async (r) => { try { await api.delete(`${base}/${r.id}`); load(); } catch (e) { toast.error(fmtErr(e)); } };

  return (
    <div className="max-w-xl">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${isCat ? "category" : "specialty"} name`} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`tax-input-${kind}`} />
        <Button onClick={add} className="bg-ink hover:bg-black" data-testid={`tax-add-${kind}`}><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <div className="mt-4 divide-y divide-zinc-100 border border-zinc-200 rounded-lg">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-3 py-2" data-testid={`tax-row-${r.id}`}>
            <span className={`text-sm ${r.active ? "" : "text-zinc-400 line-through"}`}>{r.name}</span>
            <div className="flex items-center gap-2">
              <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} />
              <Button variant="ghost" size="icon" onClick={() => del(r)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiPrefill({ onCreated }) {
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const run = async () => {
    const list = urls.split("\n").map((u) => u.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste at least one website URL"); return; }
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post("/marketplace/admin/ai-prefill", { urls: list });
      setResults(data.results);
      if (data.created) { toast.success(`${data.created} supplier(s) created`); onCreated(); }
      else toast.error("No suppliers could be created — see results below");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-3" data-testid="ai-prefill-panel">
      <p className="text-sm text-zinc-600">
        Paste company website URLs (one per line, max 10). The AI crawls each site, extracts the
        company profile and creates an <b>offline</b> supplier — only when an email address is
        published on the site.
      </p>
      <textarea
        value={urls} onChange={(e) => setUrls(e.target.value)}
        placeholder={"https://example-marine.ae\nhttps://another-boatyard.com"}
        rows={5} disabled={busy}
        className="w-full rounded-md border border-zinc-200 p-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ink"
        data-testid="ai-prefill-urls"
      />
      <Button onClick={run} disabled={busy} className="bg-ink hover:bg-black" data-testid="ai-prefill-run">
        {busy ? "Analyzing sites…" : "Create suppliers with AI"}
      </Button>
      {results && (
        <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="ai-prefill-results">
          {results.map((r, i) => (
            <div key={i} className="px-4 py-3 text-sm flex items-start gap-2">
              {r.ok ? <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <Ban className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium truncate">{r.ok ? `${r.supplier.company_name} (${r.supplier.supplier_no})` : r.url}</div>
                <div className="text-xs text-zinc-500">{r.ok ? `${r.supplier.email} · ${[r.supplier.city, r.supplier.country].filter(Boolean).join(", ")}` : r.reason}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MarketplaceAdmin() {
  const { user, loading } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadSuppliers = async () => {
    try {
      const { data } = await api.get("/marketplace/admin/suppliers", { params: statusFilter ? { status: statusFilter } : {} });
      setSuppliers(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => {
    api.get("/marketplace/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/marketplace/admin/specialties").then((r) => setSpecialties(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSuppliers(); /* eslint-disable-next-line */ }, [statusFilter]);

  const setStatus = async (s, status, featured) => {
    try { await api.post(`/marketplace/admin/suppliers/${s.id}/status`, { status, ...(featured != null ? { featured } : {}) }); loadSuppliers(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (s) => { try { await api.delete(`/marketplace/admin/suppliers/${s.id}`); loadSuppliers(); toast.success("Deleted"); } catch (e) { toast.error(fmtErr(e)); } };
  const removeDemo = async () => {
    if (!window.confirm("Remove all demo suppliers and the demo provider login? They won't reappear on restart.")) return;
    try { const r = await api.post("/marketplace/admin/demo/remove"); loadSuppliers(); toast.success(`Removed ${r.data.removed} demo supplier(s)`); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const restoreDemo = async () => {
    try { await api.post("/marketplace/admin/demo/restore"); loadSuppliers(); toast.success("Demo data restored"); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const hasDemo = suppliers.some((s) => s.is_demo);

  if (loading) return null;
  if (!canManageMarketplace(user)) return <Navigate to="/app" replace />;

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="marketplace-admin-page">
      <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2 text-zinc-500">
        <Link to="/app/marketplace"><ArrowLeft className="h-4 w-4 mr-1" />Back to marketplace</Link>
      </Button>
      <h1 className="font-display text-3xl">Marketplace admin</h1>

      <Tabs defaultValue="suppliers" className="mt-6">
        <TabsList>
          <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="specialties" data-testid="admin-tab-specialties">Specialties</TabsTrigger>
          <TabsTrigger value="ai-prefill" data-testid="admin-tab-ai-prefill">AI Prefill</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="mt-4">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-zinc-200 px-2 text-sm" data-testid="admin-status-filter">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="suspended">Suspended</option>
              <option value="rejected">Rejected</option>
            </select>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-ink hover:bg-black" data-testid="admin-new-supplier"><Plus className="h-4 w-4 mr-1" />New supplier</Button>
            <div className="flex items-center gap-2">
              {hasDemo ? (
                <Button variant="outline" onClick={removeDemo} data-testid="admin-remove-demo" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-1" />Remove all demo data</Button>
              ) : (
                <Button variant="outline" onClick={restoreDemo} data-testid="admin-restore-demo">Restore demo data</Button>
              )}
            </div>
          </div>

          <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {suppliers.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm">No suppliers yet.</div>}
            {suppliers.map((s) => (
              <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3" data-testid={`admin-supplier-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate min-w-0">{s.company_name}</span>
                    <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || ""}`}>{s.status}</span>
                    {s.featured && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 stroke-amber-400" />}
                    {s.is_demo && <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Demo</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{[s.city, s.country].filter(Boolean).join(", ")} · {(s.categories || []).map((c) => c.name).join(", ") || "no categories"}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  {s.status !== "approved" && <Button size="sm" variant="outline" onClick={() => setStatus(s, "approved")} data-testid={`approve-${s.id}`}><Check className="h-3.5 w-3.5 mr-1 text-emerald-600" />Approve</Button>}
                  {s.status === "approved" && <Button size="sm" variant="outline" onClick={() => setStatus(s, "approved", !s.featured)} title="Toggle featured"><Star className={`h-3.5 w-3.5 ${s.featured ? "fill-amber-400 stroke-amber-400" : ""}`} /></Button>}
                  {s.status !== "suspended" && <Button size="sm" variant="outline" onClick={() => setStatus(s, "suspended")}><Ban className="h-3.5 w-3.5 mr-1" />Suspend</Button>}
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setFormOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-4"><TaxonomyManager kind="categories" /></TabsContent>
        <TabsContent value="specialties" className="mt-4"><TaxonomyManager kind="specialties" /></TabsContent>
        <TabsContent value="ai-prefill" className="mt-4"><AiPrefill onCreated={loadSuppliers} /></TabsContent>
      </Tabs>

      <SupplierForm
        open={formOpen} onClose={() => setFormOpen(false)} editing={editing}
        categories={categories} specialties={specialties} onSaved={loadSuppliers}
      />
    </div>
  );
}
